/**
 * Declarative layout for the raw Balance Sheet Statement table.
 *
 * Row order is explicit — never inferred from JSON key insertion order.
 * Lookup keys match stored balanceSheet object labels exactly.
 */

import { parseCurrencyValue } from '../utils/financialValueParse';

export const OTHER_ASSETS_CHILD_LABELS = [
    'Inventories',
    'Trade receivables',
    'Cash Equivalents',
    'Loans n Advances',
    'Other asset items',
];

export const TRADE_RECEIVABLES_CHILD_LABELS = [
    'Receivables over 6m',
    'Receivables under 6m',
    'Prov for Doubtful',
];

export const BALANCE_SHEET_STATEMENT_SECTIONS = [
    {
        id: 'equityCapital',
        totalRowLabel: 'Equity Capital',
        collapsible: false,
        totalRole: 'plain',
    },
    {
        id: 'reserves',
        totalRowLabel: 'Reserves',
        collapsible: false,
        totalRole: 'plain',
    },
    {
        id: 'borrowings',
        totalRowLabel: 'Borrowings',
        driverRowLabels: [
            'Long term Borrowings',
            'Short term Borrowings',
            'Lease Liabilities',
            'Other Borrowings',
        ],
    },
    {
        id: 'otherLiabilities',
        totalRowLabel: 'Other Liabilities',
        driverRowLabels: [
            'Non controlling int',
            'Trade Payables',
            'Other liability items',
        ],
    },
    {
        id: 'totalLiabilities',
        totalRowLabel: 'Total Liabilities',
        collapsible: false,
        totalRole: 'grandTotal',
    },
    {
        id: 'fixedAssets',
        totalRowLabel: 'Fixed Assets',
        driverRowLabels: [
            'Land',
            'Building',
            'Plant Machinery',
            'Equipments',
            'Furniture n fittings',
            'Vehicles',
            'Intangible Assets',
            'Other fixed assets',
        ],
        scheduleSubtotalRowLabels: [
            'Gross Block',
            'Accumulated Depreciation',
        ],
    },
    {
        id: 'cwip',
        totalRowLabel: 'CWIP',
        collapsible: false,
        totalRole: 'plain',
    },
    {
        id: 'investments',
        totalRowLabel: 'Investments',
        collapsible: false,
        totalRole: 'plain',
    },
    {
        id: 'otherAssets',
        totalRowLabel: 'Other Assets',
        driverRowLabels: [...OTHER_ASSETS_CHILD_LABELS],
        nestedSections: [
            {
                id: 'tradeReceivables',
                totalRowLabel: 'Trade receivables',
                driverRowLabels: [...TRADE_RECEIVABLES_CHILD_LABELS],
            },
        ],
    },
    {
        id: 'totalAssets',
        totalRowLabel: 'Total Assets',
        collapsible: false,
        totalRole: 'grandTotal',
    },
];

export const BALANCE_SHEET_OTHER_ITEMS_LABEL = 'Other items';
export const BALANCE_SHEET_UNCLASSIFIED_LABEL = 'Unclassified';

function collectCollapsibleIds() {
    const ids = new Set();
    BALANCE_SHEET_STATEMENT_SECTIONS.forEach((section) => {
        if (section.collapsible !== false && (section.totalRole || 'total') === 'total') {
            ids.add(section.id);
        }
        (section.nestedSections || []).forEach((nested) => {
            if (nested.collapsible !== false) ids.add(nested.id);
        });
    });
    return ids;
}

const COLLAPSIBLE_SECTION_IDS = collectCollapsibleIds();

function collectSectionLabels(section, known) {
    if (section.totalRowLabel) known.add(section.totalRowLabel);
    (section.driverRowLabels || []).forEach((label) => known.add(label));
    if (section.subtotalRowLabel) known.add(section.subtotalRowLabel);
    (section.trailingDriverRowLabels || []).forEach((label) => known.add(label));
    (section.scheduleSubtotalRowLabels || []).forEach((label) => known.add(label));
    (section.nestedSections || []).forEach((nested) => collectSectionLabels(nested, known));
}

function collectKnownLabels() {
    const known = new Set();
    BALANCE_SHEET_STATEMENT_SECTIONS.forEach((section) => collectSectionLabels(section, known));
    return known;
}

export const BALANCE_SHEET_KNOWN_ROW_LABELS = collectKnownLabels();

/**
 * Best-effort parent section for an unexpected line item.
 * Ambiguous labels stay unclassified rather than being forced into a section.
 *
 * @param {string} label
 * @returns {'borrowings' | 'otherLiabilities' | 'fixedAssets' | 'otherAssets' | 'unclassified'}
 */
export function inferUnmappedSection(label) {
    const text = String(label || '').toLowerCase();
    if (!text.trim()) return 'unclassified';

    if (/borrow|lease/.test(text)) return 'borrowings';
    if (/non controlling|nci|payable|liabilit/.test(text)) return 'otherLiabilities';
    if (/land|building|plant|equip|furniture|vehicle|intangible|fixed asset|gross block|depreci/.test(text)) {
        return 'fixedAssets';
    }
    if (/inventor|receivable|cash|loan|asset items|doubtful/.test(text)) return 'otherAssets';

    return 'unclassified';
}

function hasRow(data, label) {
    return Boolean(data) && Object.prototype.hasOwnProperty.call(data, label);
}

function pushDataRow(rows, data, label, role, sectionId, extras = {}) {
    if (!hasRow(data, label)) return;
    rows.push({
        kind: 'data',
        label,
        role,
        sectionId: sectionId || null,
        parentSectionId: extras.parentSectionId || null,
        depth: extras.depth || 0,
    });
}

function resolveTotalRole(section) {
    const role = section.totalRole || 'total';
    if (role === 'subtotal' && section.collapsible === false) return 'statementSubtotal';
    return role;
}

function pushOtherItems(rows, extras, sectionId, parentSectionId = null, depth = 0) {
    if (!extras || !extras.length) return;
    rows.push({
        kind: 'groupLabel',
        label: BALANCE_SHEET_OTHER_ITEMS_LABEL,
        role: 'groupLabel',
        sectionId: sectionId || null,
        parentSectionId,
        depth,
    });
    extras.forEach((label) => {
        rows.push({
            kind: 'data',
            label,
            role: 'other',
            sectionId: sectionId || null,
            parentSectionId,
            depth,
        });
    });
}

function nestedHasChildren(data, nested) {
    return (nested.driverRowLabels || []).some((label) => hasRow(data, label));
}

function pushSectionBody(rows, data, section, parentSectionId = null, depth = 0) {
    const nestedByLabel = new Map(
        (section.nestedSections || []).map((nested) => [nested.totalRowLabel, nested]),
    );

    (section.driverRowLabels || []).forEach((label) => {
        const nested = nestedByLabel.get(label);
        if (nested && nestedHasChildren(data, nested)) {
            pushDataRow(rows, data, nested.totalRowLabel, 'total', nested.id, {
                parentSectionId: section.id,
                depth: depth + 1,
            });
            (nested.driverRowLabels || []).forEach((childLabel) => {
                pushDataRow(rows, data, childLabel, 'driver', nested.id, {
                    parentSectionId: section.id,
                    depth: depth + 2,
                });
            });
            return;
        }
        pushDataRow(rows, data, label, 'driver', section.id, { parentSectionId, depth: depth + 1 });
    });

    if (section.subtotalRowLabel) {
        pushDataRow(rows, data, section.subtotalRowLabel, 'subtotal', section.id, {
            parentSectionId,
            depth: depth + 1,
        });
    }
    (section.trailingDriverRowLabels || []).forEach((label) => {
        pushDataRow(rows, data, label, 'driver', section.id, { parentSectionId, depth: depth + 1 });
    });
    (section.scheduleSubtotalRowLabels || []).forEach((label) => {
        pushDataRow(rows, data, label, 'subtotal', section.id, { parentSectionId, depth: depth + 1 });
    });
}

/**
 * Flatten balance-sheet JSON into presentation rows.
 * Missing mapped labels are omitted. Unmapped labels are never dropped.
 *
 * @param {Record<string, unknown>|null|undefined} balanceSheet
 * @returns {Array<{
 *   kind: 'data' | 'groupLabel' | 'divider',
 *   label: string,
 *   role: 'total' | 'driver' | 'subtotal' | 'statementSubtotal' | 'plain' | 'other' | 'grandTotal' | 'supplementary' | 'groupLabel' | 'divider',
 *   sectionId: string|null,
 *   parentSectionId: string|null,
 *   depth: number,
 * }>}
 */
export function buildBalanceSheetStatementRows(balanceSheet) {
    const data = balanceSheet && typeof balanceSheet === 'object' ? balanceSheet : {};
    const unmappedBySection = {
        borrowings: [],
        otherLiabilities: [],
        fixedAssets: [],
        otherAssets: [],
        unclassified: [],
    };

    Object.keys(data).forEach((label) => {
        if (BALANCE_SHEET_KNOWN_ROW_LABELS.has(label)) return;
        const sectionId = inferUnmappedSection(label);
        unmappedBySection[sectionId].push(label);
    });

    const rows = [];

    BALANCE_SHEET_STATEMENT_SECTIONS.forEach((section) => {
        pushDataRow(rows, data, section.totalRowLabel, resolveTotalRole(section), section.id);
        pushSectionBody(rows, data, section);

        const extras = unmappedBySection[section.id];
        if (extras && extras.length) {
            pushOtherItems(rows, extras, section.id, null, 1);
        }
    });

    if (unmappedBySection.unclassified.length) {
        rows.push({
            kind: 'groupLabel',
            label: BALANCE_SHEET_UNCLASSIFIED_LABEL,
            role: 'groupLabel',
            sectionId: null,
            parentSectionId: null,
            depth: 0,
        });
        unmappedBySection.unclassified.forEach((label) => {
            rows.push({
                kind: 'data',
                label,
                role: 'other',
                sectionId: null,
                parentSectionId: null,
                depth: 0,
            });
        });
    }

    return rows;
}

export function isBalanceSheetSectionCollapsible(sectionId) {
    return COLLAPSIBLE_SECTION_IDS.has(sectionId);
}

/**
 * Whether a layout row should render given the section expand map.
 * Top-level section totals stay visible when collapsed; nested totals hide with their parent.
 *
 * @param {{ role: string, sectionId: string|null, parentSectionId?: string|null }} row
 * @param {Record<string, boolean>} expanded
 */
export function isBalanceSheetStatementRowVisible(row, expanded) {
    if (!row) return false;

    if (row.parentSectionId && isBalanceSheetSectionCollapsible(row.parentSectionId)) {
        if (expanded?.[row.parentSectionId] === false) return false;
    }

    if (row.role === 'total' && !row.parentSectionId) return true;
    if (
        row.role === 'grandTotal'
        || row.role === 'supplementary'
        || row.role === 'divider'
        || row.role === 'plain'
        || row.role === 'statementSubtotal'
    ) {
        return true;
    }
    if (row.role === 'total' && row.parentSectionId) return true;

    if (row.sectionId && isBalanceSheetSectionCollapsible(row.sectionId)) {
        return expanded?.[row.sectionId] !== false;
    }
    return true;
}

/**
 * Compare Total Liabilities vs Total Assets for each comparable year.
 * Exact numeric equality after parseCurrencyValue; years missing either side are skipped.
 *
 * @param {Record<string, unknown>|null|undefined} balanceSheet
 * @param {string[]} orderedKeys
 * @returns {{ balanced: boolean, mismatchedYears: string[], comparedCount: number }}
 */
export function evaluateBalanceSheetBalance(balanceSheet, orderedKeys = []) {
    const data = balanceSheet && typeof balanceSheet === 'object' ? balanceSheet : {};
    const liabilities = data['Total Liabilities'] || {};
    const assets = data['Total Assets'] || {};
    const mismatchedYears = [];
    let comparedCount = 0;

    orderedKeys.forEach((year) => {
        const liabilityValue = parseCurrencyValue(liabilities[year]);
        const assetValue = parseCurrencyValue(assets[year]);
        if (liabilityValue == null || assetValue == null) return;
        comparedCount += 1;
        if (liabilityValue !== assetValue) {
            mismatchedYears.push(year);
        }
    });

    return {
        balanced: comparedCount > 0 && mismatchedYears.length === 0,
        mismatchedYears,
        comparedCount,
    };
}

/**
 * Sum Other Assets children vs the reported Other Assets total.
 * Years missing the parent total are skipped. Default tolerance is 2 crore (Screener rounding).
 *
 * @param {Record<string, unknown>|null|undefined} balanceSheet
 * @param {string[]} years
 * @param {number} [tolerance=2]
 * @returns {{
 *   years: Array<{ year: string, parent: number, childSum: number, delta: number, ok: boolean }>,
 *   allOk: boolean,
 * }}
 */
export function reconcileOtherAssets(balanceSheet, years = [], tolerance = 2) {
    const data = balanceSheet && typeof balanceSheet === 'object' ? balanceSheet : {};
    const results = [];

    years.forEach((year) => {
        const parent = parseCurrencyValue(data['Other Assets']?.[year]);
        if (parent == null) return;

        const childSum = OTHER_ASSETS_CHILD_LABELS.reduce((sum, label) => {
            const value = parseCurrencyValue(data[label]?.[year]);
            return value == null ? sum : sum + value;
        }, 0);

        const delta = Math.abs(parent - childSum);
        results.push({
            year,
            parent,
            childSum,
            delta,
            ok: delta <= tolerance,
        });
    });

    return {
        years: results,
        allOk: results.length > 0 && results.every((entry) => entry.ok),
    };
}
